import { Check, Close, Download, Edit } from "@mui/icons-material";
import {
  Fade,
  IconButton,
  lighten,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { format } from "date-fns";
import { ChangeEvent, useCallback, useMemo, useState } from "react";
import {
  useGenerateNotePdfMutation,
  useUpdateClinicalNoteMutation,
} from "src/api/services/clinicalNotesApi";
import { ClinicalNotes, NoteTypeEnums } from "src/api/types";
import { theme } from "src/assets/theme";
import { useSentryLogger } from "src/hooks/useSentryLogger";

interface Props {
  note: ClinicalNotes;
  idx: number;
  noteType: NoteTypeEnums;
}
interface NoteTextProps {
  note: string | null;
  isHtml: boolean;
}

const NoteText = ({ note, isHtml }: NoteTextProps) => {
  if (isHtml) {
    return (
      <Typography
        component={"p"}
        dangerouslySetInnerHTML={{
          __html: String(note),
        }}
      />
    );
  }

  return <Typography>{note}</Typography>;
};

const Note = ({ note, idx, noteType }: Props) => {
  const isHtml = useMemo(() => note.note?.includes("<p>") ?? false, [note]);
  const [generatePdf] = useGenerateNotePdfMutation();
  const [updateNote, { isLoading }] = useUpdateClinicalNoteMutation();
  const [edit, setEdit] = useState<boolean>(false);
  const [newNote, setNewNote] = useState<string>("");

  const errLog = useSentryLogger("error");

  const noteInformation = useCallback((note: ClinicalNotes) => {
    const clinicianFullName = `${note.profile.first_name} ${note.profile.last_name}`;
    const dateWritten = format(new Date(note.created_at), "Pp");

    return `${clinicianFullName} - ${dateWritten}`;
  }, []);

  const handleEdit = useCallback(() => {
    setEdit((e) => !e);
  }, []);

  const handleNewNote = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setNewNote(e.target.value);
  }, []);

  const handleGeneratePdf = useCallback(async () => {
    if (note.note == null) return;

    try {
      const blob = await generatePdf(note.note).unwrap();

      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");

      a.href = url;
      a.download = "note.pdf";
      a.click();

      URL.revokeObjectURL(url);
    } catch (err) {
      errLog("Could not generate pdf for selected note", err);
    }
  }, [errLog, generatePdf, note]);

  const handleUpdate = useCallback(async () => {
    if (newNote.length === 0) return;

    await updateNote({
      id: note.id,
      body: {
        note: newNote,
      },
    }).then(() => {
      setNewNote("");
      setEdit(false);
    });
  }, [newNote, note.id, updateNote]);

  return (
    <Stack gap={1}>
      <Stack
        sx={{
          bgcolor:
            idx % 2 === 1
              ? theme.palette.grey[100]
              : lighten(theme.palette.primary.main, 0.6),
          p: 1,
          borderRadius: 1,
        }}
        direction={"row"}
        justifyContent={"space-between"}
        alignItems={"flex-start"}
      >
        <NoteText isHtml={isHtml} note={note.note} />
        {noteType === "Provider" ? (
          <IconButton color="info" onClick={handleGeneratePdf}>
            <Download />
          </IconButton>
        ) : null}
      </Stack>
      <Fade in={edit} timeout={300} unmountOnExit>
        <Stack direction={"row"}>
          <TextField
            onChange={handleNewNote}
            multiline
            value={newNote}
            placeholder="Edit note"
            fullWidth
          />
          <IconButton
            size="small"
            disabled={isLoading}
            onClick={handleUpdate}
            color="success"
          >
            <Check />
          </IconButton>
        </Stack>
      </Fade>
      <Stack>
        <Stack direction={"row"} alignItems={"center"} gap={1} alignSelf="end">
          <IconButton
            color={edit ? "error" : "secondary"}
            size="small"
            onClick={handleEdit}
          >
            {edit ? <Close /> : <Edit />}
          </IconButton>
          <Typography variant="subtitle2">{noteInformation(note)}</Typography>
        </Stack>
        {note.updated_at != null ? (
          <Typography variant="subtitle2" alignSelf="end">
            Updated at: {format(new Date(note.updated_at), "Pp")}
          </Typography>
        ) : null}
      </Stack>
    </Stack>
  );
};

export default Note;
